import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, HostListener, OnInit, OnChanges, SimpleChanges, ViewChild, AfterViewInit, ChangeDetectorRef, Injector, DoCheck } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface SearchableDropdownOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  tooltip?: string; // Optional tooltip/description for hover
  rightLabel?: string; // Optional right-side label (e.g., DOB) to display on the right side of the option
  /** Optional display text for selected value (input). If omitted, uses `label`. */
  displayLabel?: string;
}

@Component({
  selector: 'app-searchable-input-dropdown',
  standalone: false,
  templateUrl: './searchable-input-dropdown.component.html',
  styleUrl: './searchable-input-dropdown.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableInputDropdownComponent),
      multi: true
    }
  ]
})
export class SearchableInputDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, DoCheck {
  @Input() label?: string;
  @Input() placeholder: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() multiple: boolean = false;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() options: SearchableDropdownOption[] = [];
  @Input() debounceTime: number = 300; // Debounce time for search
  /** When true, options come from server search — do not filter labels locally (avoids empty list after API returns). */
  @Input() serverSideSearch: boolean = false;
  @Input() isLoading: boolean = false; // Loading state from parent
  @Input() enablePagination: boolean = false; // Enable scroll pagination
  @Input() hasMore: boolean = false; // Whether there are more items to load
  @Input() showAllOnFocus: boolean = true; // Show all options on focus (default true)
  @Input() preferOpenAbove: boolean = false; // Prefer opening dropdown above input (useful for fields at bottom of modal)
  /** When set, used as display text when value is set but no matching option (e.g. full plan name for stored planId) */
  @Input() displayLabelForValue?: string;
  @Input() icon: string = ''; // Icon class (e.g., 'fas fa-edit')
  @Input() iconColor: string = 'text-primary'; // Icon color class
  @Input() iconDisabled: boolean = false; // When true, icon is visible but not clickable (e.g. until value is set)
  @Input() iconTitle: string = '';
  /**
   * Max height (px) of the options panel when open. Use a smaller value (e.g. 180) for compact lists (~4 rows).
   */
  @Input() panelMaxHeightPx: number = 350;

  @Output() searchQuery = new EventEmitter<string>(); // Emit search query for API calls
  @Output() loadMore = new EventEmitter<void>(); // Emit when scrolling to bottom (for pagination)
  @Output() valueChange = new EventEmitter<string | number | (string | number)[]>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<void>(); // Emit when input receives focus
  @Output() iconClick = new EventEmitter<void>(); // Emit when icon is clicked

  searchTerm: string = '';
  filteredOptions: SearchableDropdownOption[] = [];
  isOpen: boolean = false;
  selectedValues: any[] = [];
  value: string | number | (string | number)[] = '';
  isTouched: boolean = false;
  isFocused: boolean = false;
  dropdownStyle: any = {};
  openAbove: boolean = false;
  displayValue: string = '';
  private isUpdatingFromForm: boolean = false;
  private control: AbstractControl | null = null;
  private isLoadingMore: boolean = false; // Track if loading more for pagination

  @ViewChild('inputElement', { static: false }) inputElement?: any;
  @ViewChild('dropdownContainer', { static: false }) dropdownContainer?: ElementRef;

  private searchSubject = new Subject<string>();
  private onChange = (value: any) => { };
  private onTouched = () => { };

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private injector: Injector
  ) {
    // Try to get the form control from the injector
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl) {
        this.control = ngControl.control;
      }
    } catch (e) {
      // NgControl not available, which is fine
    }
  }

  ngAfterViewInit(): void {
    // Sync initial value
    this.syncInputValue();
  }

  ngDoCheck(): void {
    // Sync component's touched state with form control's touched state
    if (this.control && this.control.touched && !this.isTouched) {
      this.isTouched = true;
      this.cdr.markForCheck();
    }
  }

  private syncInputValue(): void {
    if (this.inputElement && this.inputElement.nativeElement) {
      this.inputElement.nativeElement.value = this.displayValue || '';
    }
  }

  ngOnInit(): void {
    this.filteredOptions = this.options;
    if (this.multiple && Array.isArray(this.value)) {
      this.selectedValues = [...this.value];
    }

    // If we have displayLabelForValue but no value, set displayValue to show it
    if (!this.multiple && (!this.value || this.value === '') && this.displayLabelForValue != null && String(this.displayLabelForValue).trim() !== '') {
      this.displayValue = String(this.displayLabelForValue).trim();
    }

    // Server-backed lists should react quickly; keep client debounce for local-only filtering.
    const debounceMs = this.serverSideSearch
      ? Math.min(this.debounceTime ?? 300, 120)
      : (this.debounceTime ?? 300);

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.onSearch(searchTerm);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      // When options change, check if we're appending (pagination) or replacing
      const previousOptions = changes['options'].previousValue || [];
      const currentOptions = changes['options'].currentValue || [];

      // If new options are more than previous, we're appending (pagination)
      // Otherwise, we're replacing (new search or initial load)
      if (currentOptions.length > previousOptions.length && this.enablePagination) {
        // Appending: preserve scroll position by not resetting filteredOptions
        // Just update filteredOptions with new options
        this.filteredOptions = this.options;
        this.filterLocalOptions();
        // Reset loading more flag after a short delay to allow DOM update
        setTimeout(() => {
          this.isLoadingMore = false;
        }, 100);
      } else {
        // Replacing: reset filteredOptions normally
        this.filteredOptions = this.options;
        this.filterLocalOptions();
        this.isLoadingMore = false;
      }

      // When API returns options while user is typing, show the list immediately.
      if (this.serverSideSearch && (this.options?.length ?? 0) > 0 && (this.isFocused || (this.searchTerm && this.searchTerm.trim().length > 0))) {
        this.isOpen = true;
        queueMicrotask(() => this.calculateDropdownPosition());
      }

      if (!this.multiple && this.value && !this.isFocused) {
        const selectedOption = this.options.find((opt) => this.optionValueEquals(opt.value, this.value));
        if (selectedOption) {
          this.displayValue = selectedOption.displayLabel ?? selectedOption.label;
          this.syncInputValue();
        } else if (
          this.displayLabelForValue != null &&
          String(this.displayLabelForValue).trim() !== ''
        ) {
          this.displayValue = String(this.displayLabelForValue).trim();
          this.syncInputValue();
        }
      }

      this.cdr.detectChanges();
    }

    if (changes['isLoading']) {
      // When loading state changes, reset loading more flag if loading is complete
      if (!changes['isLoading'].currentValue) {
        setTimeout(() => {
          this.isLoadingMore = false;
        }, 100);
      }
      // Loading finished: reopen panel so new options are visible without another click.
      if (
        this.serverSideSearch &&
        changes['isLoading'].previousValue === true &&
        changes['isLoading'].currentValue === false &&
        (this.isFocused || (this.searchTerm && this.searchTerm.trim().length > 0))
      ) {
        this.isOpen = true;
        this.filterLocalOptions();
        queueMicrotask(() => this.calculateDropdownPosition());
        this.cdr.detectChanges();
      }
    }

    if (changes['value']) {
      if (this.multiple && Array.isArray(this.value)) {
        this.selectedValues = [...this.value];
      }
      this.cdr.detectChanges();
    }
    if (changes['displayLabelForValue'] && !this.multiple) {
      // If we have displayLabelForValue, show it even if value is empty
      if (this.displayLabelForValue != null && String(this.displayLabelForValue).trim() !== '') {
        if (this.value) {
          const selectedOption = this.options.find(opt => this.optionValueEquals(opt.value, this.value));
          if (!selectedOption) {
            this.displayValue = String(this.displayLabelForValue).trim();
            this.syncInputValue();
          }
        } else {
          // No value but we have displayLabelForValue - show it
          this.displayValue = String(this.displayLabelForValue).trim();
          this.syncInputValue();
        }
      } else if (!this.value) {
        this.displayValue = '';
        this.searchTerm = '';
        this.syncInputValue();
      }
      this.cdr.detectChanges();
    }
  }

  onInputChange(event: Event): void {
    // Get the current input value directly from the element
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    // Update our internal state
    this.displayValue = inputValue;
    this.searchTerm = inputValue;

    // For single select, if user is typing, clear selection
    if (!this.multiple && this.hasValue && inputValue !== this.getDisplayText()) {
      // User is typing something different, allow it
      this.value = '';
      this.onChange('');
      this.valueChange.emit('');
    }

    // Emit search query
    this.searchSubject.next(this.searchTerm);

    // Show dropdown when typing
    if (inputValue.length > 0) {
      this.isOpen = true;
      this.calculateDropdownPosition();
    } else {
      // Show all options when input is empty ONLY if showAllOnFocus is true
      if (this.showAllOnFocus) {
        this.filteredOptions = this.options;
        this.isOpen = true;
        this.calculateDropdownPosition();
      } else {
        this.isOpen = false;
      }
    }

    // Ensure input value is preserved
    setTimeout(() => {
      if (this.inputElement && this.inputElement.nativeElement) {
        const currentValue = this.inputElement.nativeElement.value;
        if (currentValue !== inputValue) {
          this.inputElement.nativeElement.value = inputValue;
        }
      }
    }, 0);
  }

  onKeyUp(event: KeyboardEvent): void {
    // Allow all keys - don't prevent default
    // This ensures typing works properly
  }

  onInputFocus(): void {
    this.isFocused = true;
    // Only open if showAllOnFocus is true or we have a search term
    if (this.showAllOnFocus || (this.searchTerm && this.searchTerm.length > 0)) {
      // Show all pre-loaded options when opening on focus
      if (!this.searchTerm || this.searchTerm.trim() === '') {
        this.filteredOptions = this.options;
      }
      this.isOpen = true;
      this.calculateDropdownPosition();
    }

    // Emit focus event so parent can fetch initial data
    this.focus.emit();

    // Ensure input value is synced
    this.syncInputValue();
  }

  onInputBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.isTouched = true;
    this.onTouched();

    // Sync touched state with control
    if (this.control) {
      this.control.markAsTouched();
      this.cdr.markForCheck();
    }

    // Delay closing to allow option selection
    setTimeout(() => {
      const activeElement = document.activeElement;
      const dropdownMenu = this.elementRef.nativeElement.querySelector('.dropdown-menu');

      // Don't close if clicking on dropdown
      if (!dropdownMenu || !dropdownMenu.contains(activeElement)) {
        this.isOpen = false;
        this.blur.emit(event);

        // For single select, if no selection made and user typed something, keep the typed value (do not clear - only Reset clears)
        if (!this.multiple && !this.hasValue && this.searchTerm) {
          // User typed but didn't select - keep the search term in display
          this.displayValue = this.searchTerm;
          this.syncInputValue();
        } else if (!this.multiple && this.hasValue) {
          // Update display value to show selected option; if option not in list (e.g. options refreshed), keep current display
          const label = this.getDisplayText();
          if (label !== '') {
            this.displayValue = label;
          }
          // else keep existing displayValue so selection is not cleared when clicking outside
          this.syncInputValue();
        }
      }
    }, 200);
  }

  onKeyDown(event: KeyboardEvent): void {
    // Allow Escape to close dropdown
    if (event.key === 'Escape') {
      this.isOpen = false;
      if (!this.multiple && this.hasValue) {
        this.displayValue = this.getDisplayText();
      }
    }
    // Don't prevent default for normal typing
    // Allow Enter to select first option (optional)
    // if (event.key === 'Enter' && this.filteredOptions.length > 0) {
    //   event.preventDefault();
    //   this.selectOption(this.filteredOptions[0], event);
    // }
  }

  private onSearch(searchTerm: string): void {
    const term = searchTerm.trim();

    if (term.length === 0) {
      this.filteredOptions = this.options;
      this.searchQuery.emit('');
      return;
    }

    // Emit search query for API-based search
    this.searchQuery.emit(term);

    // Filter local options
    this.filterLocalOptions();
  }

  private filterLocalOptions(): void {
    if (this.serverSideSearch) {
      this.filteredOptions = [...this.options];
      return;
    }
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredOptions = this.options;
      return;
    }

    const termLower = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter(option =>
      option.label.toLowerCase().includes(termLower)
    );
  }

  /**
   * Track by function for options to prevent unnecessary re-renders
   */
  trackByOption(index: number, option: SearchableDropdownOption): any {
    return option.value;
  }

  selectOption(option: SearchableDropdownOption, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.multiple) {
      this.toggleSelection(option);
      // Keep dropdown open for multiple select
      // Clear search term but keep input focused
      this.searchTerm = '';
      this.displayValue = '';
      const inputElement = this.elementRef.nativeElement.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    } else {
      // Single select
      this.value = option.value;
      this.displayValue = option.displayLabel ?? option.label;
      this.searchTerm = '';
      this.isFocused = false; // Reset focus state so display updates immediately
      this.onChange(option.value);
      this.valueChange.emit(option.value);
      this.isOpen = false;
      this.onTouched();
      // Immediately update the input field to show selected value
      this.syncInputValue();
      // Trigger change detection to update UI immediately
      this.cdr.detectChanges();
    }
  }

  clearSelection(): void {
    this.value = '';
    this.displayValue = '';
    this.searchTerm = '';
    this.onChange('');
    this.valueChange.emit('');
    this.isOpen = true;
    this.calculateDropdownPosition();
    const inputElement = this.elementRef.nativeElement.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }

  getOptionLabel(value: string | number): string {
    const option = this.options.find(opt => this.optionValueEquals(opt.value, value));
    if (option) return option.label;
    if (this.displayLabelForValue != null && this.optionValueEquals(this.value, value)) {
      return String(this.displayLabelForValue).trim();
    }
    return String(value);
  }

  toggleSelection(option: SearchableDropdownOption): void {
    const index = this.selectedValues.indexOf(option.value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
    } else {
      this.selectedValues.push(option.value);
    }

    this.value = [...this.selectedValues];
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  isSelected(option: SearchableDropdownOption): boolean {
    if (this.multiple) {
      return this.selectedValues.includes(option.value);
    }
    return this.value === option.value;
  }

  removeSelected(value: string | number, event: Event): void {
    event.stopPropagation();
    const index = this.selectedValues.indexOf(value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
      this.value = [...this.selectedValues];
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    }
  }

  getDisplayText(): string {
    if (this.multiple) {
      return ''; // Multiple select doesn't show text in input
    } else {
      // If we have displayLabelForValue but no value, show the display label
      if ((!this.value || this.value === '') && this.displayLabelForValue != null && String(this.displayLabelForValue).trim() !== '') {
        return String(this.displayLabelForValue).trim();
      }
      if (!this.value || this.value === '') {
        return '';
      }
      const selectedOption = this.options.find(opt => this.optionValueEquals(opt.value, this.value));
      if (selectedOption) return selectedOption.displayLabel ?? selectedOption.label;
      if (this.displayLabelForValue != null && String(this.displayLabelForValue).trim() !== '') {
        return String(this.displayLabelForValue).trim();
      }
      return '';
    }
  }

  private optionValueEquals(a: string | number | (string | number)[], b: string | number | (string | number)[]): boolean {
    const aVal = Array.isArray(a) ? a[0] : a;
    const bVal = Array.isArray(b) ? b[0] : b;
    if (aVal === undefined || bVal === undefined) return false;
    if (aVal === bVal) return true;
    const na = Number(aVal);
    const nb = Number(bVal);
    return !isNaN(na) && !isNaN(nb) && na === nb;
  }

  private calculateDropdownPosition(): void {
    const inputElement = this.elementRef.nativeElement.querySelector('input');
    if (!inputElement) return;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Find the closest modal/popup container to constrain dropdown within it
    const modalContainer = this.elementRef.nativeElement.closest('.bg-white.rounded-lg') ||
                           this.elementRef.nativeElement.closest('app-modal') ||
                           this.elementRef.nativeElement.closest('.modal-content');
    
    let containerTop = 0;
    let containerBottom = viewportHeight;
    
    if (modalContainer) {
      const containerRect = modalContainer.getBoundingClientRect();
      containerTop = containerRect.top + 10; // Add padding from modal top
      containerBottom = containerRect.bottom;
    }
    
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top - containerTop; // Space above within modal
    const cap = Math.max(80, this.panelMaxHeightPx || 350);
    const estimatedDropdownHeight = cap;

    // Determine if dropdown should open above:
    // 1. If preferOpenAbove is set and there's enough space above
    // 2. Or if there's not enough space below and more space above
    this.openAbove = (this.preferOpenAbove && spaceAbove >= estimatedDropdownHeight) ||
                     (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow);

    /** Must stack above `app-modal` backdrop (100010). */
    const overlayZ = '100020';

    if (this.openAbove) {
      // Use available space above within modal (with reasonable max)
      const maxHeightAbove = Math.min(spaceAbove - 10, cap);
      this.dropdownStyle = {
        position: 'fixed',
        bottom: `${viewportHeight - rect.top + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        'max-height': `${maxHeightAbove}px`,
        'z-index': overlayZ
      };
    } else {
      // Use available space below (with reasonable max)
      const maxHeightBelow = Math.min(spaceBelow - 20, cap);
      this.dropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        'max-height': `${maxHeightBelow}px`,
        'z-index': overlayZ
      };
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.isOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  /**
   * Handle scroll event on dropdown container for pagination
   */
  onDropdownScroll(event: Event): void {
    if (!this.enablePagination || !this.hasMore || this.isLoading) {
      return;
    }

    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Load more when scrolled to within 100px of bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      // Prevent multiple simultaneous loads
      if (!this.isLoadingMore) {
        this.isLoadingMore = true;
        this.loadMore.emit();
      }
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScrollOrResize(): void {
    if (this.isOpen) {
      this.calculateDropdownPosition();
    }
  }

  private refreshDisplayForCurrentValue(): void {
    if (this.multiple || !this.value || this.isFocused) {
      return;
    }

    const selectedOption = this.options.find((opt) => this.optionValueEquals(opt.value, this.value));
    if (selectedOption) {
      const label = selectedOption.displayLabel ?? selectedOption.label;
      if (label && this.displayValue !== label) {
        this.displayValue = label;
        this.searchTerm = '';
        this.syncInputValue();
      }
      return;
    }

    const externalLabel =
      this.displayLabelForValue != null ? String(this.displayLabelForValue).trim() : '';
    if (externalLabel) {
      if (this.displayValue !== externalLabel) {
        this.displayValue = externalLabel;
        this.searchTerm = '';
        this.syncInputValue();
      }
      return;
    }

    // Never show the raw stored id (e.g. 1196) as visible text.
    if (String(this.displayValue).trim() === String(this.value).trim()) {
      this.displayValue = '';
      this.searchTerm = '';
      this.syncInputValue();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | number | (string | number)[]): void {
    this.value = value || (this.multiple ? [] : '');
    if (this.multiple && Array.isArray(this.value)) {
      this.selectedValues = [...this.value];
      // Don't change displayValue if user is typing
      if (!this.isFocused) {
        this.displayValue = '';
        this.syncInputValue();
      }
    } else if (!this.multiple && this.value) {
      const selectedOption = this.options.find(opt => this.optionValueEquals(opt.value, this.value));
      if (selectedOption) {
        this.displayValue = selectedOption.displayLabel ?? selectedOption.label;
        this.searchTerm = '';
        this.syncInputValue();
      } else if (this.displayLabelForValue != null && String(this.displayLabelForValue).trim() !== '') {
        this.displayValue = String(this.displayLabelForValue).trim();
        this.searchTerm = '';
        this.syncInputValue();
      } else {
        if (!this.isFocused) {
          // Preserve existing display when option not in list (e.g. options refreshed) so only Reset clears
          if (this.displayValue === '' || !this.value) {
            this.displayValue = '';
            this.searchTerm = '';
          } else if (String(this.displayValue).trim() === String(this.value).trim()) {
            this.displayValue = '';
            this.searchTerm = '';
          }
          this.syncInputValue();
        }
      }
      queueMicrotask(() => this.refreshDisplayForCurrentValue());
    } else {
      // When form value is cleared (e.g. Reset), clear the visible text too.
      if (this.displayLabelForValue != null && String(this.displayLabelForValue).trim() !== '') {
        this.displayValue = String(this.displayLabelForValue).trim();
      } else {
        this.displayValue = '';
      }
      this.searchTerm = '';
      this.syncInputValue();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get hasValue(): boolean {
    if (this.multiple) {
      return Array.isArray(this.selectedValues) && this.selectedValues.length > 0;
    }
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  // hasError is kept for visual styling (red border) only
  // Error messages are handled by app-form-error-message component
  get hasError(): boolean {
    return !!(this.control && this.control.invalid && (this.control.touched || this.isTouched));
  }

  onIconClick(): void {
    if (this.iconDisabled) return;
    this.isOpen = false;
    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.blur();
    }
    this.iconClick.emit();
  }
}
